import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Package, MapPin, DollarSign, AlertTriangle, Layers } from 'lucide-react';
import { partsApi, workOrdersApi } from '@/lib/api';
import { fmtDate } from '@/lib/date';
import { Button } from '@/components/ui/button';
import { Badge, statusColor } from '@/components/ui/badge';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { AttachmentsPanel } from '@/components/AttachmentsPanel';

function KpiCard({
  label, value, sub, icon: Icon, color, warn,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: typeof Package;
  color: 'teal' | 'amber' | 'green';
  warn?: boolean;
}) {
  const c = warn ? 'red' : color;
  const chip = {
    teal:  'bg-teal-700',
    amber: 'bg-amber-700',
    green: 'bg-green-700',
    red:   'bg-red-700',
  }[c];
  const border = {
    teal:  'border-l-teal-600',
    amber: 'border-l-amber-600',
    green: 'border-l-green-600',
    red:   'border-l-red-600',
  }[c];
  return (
    <div className={`rounded-lg border border-canvas-line border-l-4 ${border} bg-white px-5 py-4 flex items-start gap-3`}>
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0 text-white ${chip}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] text-slate-600 font-medium uppercase tracking-wide">{label}</p>
        <p className={`text-2xl font-semibold font-mono tracking-tight ${warn ? 'text-red-700' : 'text-slate-900'}`}>{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export function PartDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: part, isLoading: partLoading, error: partError } = useQuery({
    queryKey: ['parts', 'detail', id],
    queryFn:  () => partsApi.get(id!),
    enabled:  !!id,
  });

  const { data: wos, isLoading: wosLoading } = useQuery({
    queryKey: ['work-orders', 'by-part', id],
    queryFn:  () => workOrdersApi.list(1, 50, undefined, undefined, undefined, id!),
    enabled:  !!id,
  });

  if (partLoading) {
    return <p className="text-center text-slate-400 py-16">Loading…</p>;
  }

  if (partError || !part) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-slate-400">
        <AlertTriangle size={32} />
        <p>Part not found.</p>
        <Button variant="outline" onClick={() => navigate('/parts')}>
          <ArrowLeft size={14} /> Back to Parts
        </Button>
      </div>
    );
  }

  const isLow = part.reorder_threshold != null && part.stock_quantity <= part.reorder_threshold;

  return (
    <div className="flex flex-col gap-5">

      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate('/parts')}
          className="mt-1 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-slate-900">{part.name}</h1>
            {isLow && (
              <Badge color="red">
                <AlertTriangle size={11} className="inline mr-1" />Low Stock
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-600 font-mono mt-0.5">{part.part_number}</p>
        </div>
      </div>

      {/* Info card */}
      <Card>
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Layers size={15} className="text-teal-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Stock Qty</p>
                <p className={`font-medium ${isLow ? 'text-red-700' : 'text-slate-900'}`}>
                  {part.stock_quantity}
                  {part.reorder_threshold != null ? ` (reorder at ${part.reorder_threshold})` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={15} className="text-sky-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Location</p>
                <p className="font-medium text-slate-900">{part.location ?? '—'}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Package size={15} className="text-brand-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Supplier</p>
                <p className="font-medium text-slate-900">{part.supplier ?? '—'}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <DollarSign size={15} className="text-green-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Unit Cost</p>
                <p className="font-medium text-slate-900">
                  {part.unit_cost != null ? `$${Number(part.unit_cost).toFixed(2)}` : '—'}
                </p>
              </div>
            </div>
            {part.description && (
              <div className="col-span-2 md:col-span-4">
                <p className="text-xs text-slate-600 font-medium mb-0.5">Description</p>
                <p className="text-slate-700">{part.description}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiCard
          label="Current Stock"
          value={String(part.stock_quantity)}
          sub="units on hand"
          icon={Layers}
          color="teal"
          warn={isLow}
        />
        <KpiCard
          label="Reorder At"
          value={part.reorder_threshold != null ? String(part.reorder_threshold) : '—'}
          sub="units threshold"
          icon={AlertTriangle}
          color="amber"
        />
        <KpiCard
          label="Unit Cost"
          value={part.unit_cost != null ? `$${Number(part.unit_cost).toFixed(2)}` : '—'}
          sub={
            part.unit_cost != null && part.stock_quantity > 0
              ? `$${(Number(part.unit_cost) * part.stock_quantity).toFixed(2)} total value`
              : undefined
          }
          icon={DollarSign}
          color="green"
        />
      </div>

      {/* Work orders that used this part */}
      <Card>
        <CardHeader title={`Work Orders Using This Part (${wos?.pagination.total ?? 0})`} />
        <CardBody className="p-0">
          {wosLoading ? (
            <p className="px-5 py-6 text-center text-slate-400 text-sm">Loading…</p>
          ) : (wos?.data.length ?? 0) === 0 ? (
            <p className="px-5 py-6 text-center text-slate-400 text-sm">
              No work orders have used this part yet.
            </p>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>WO #</Th>
                  <Th>Title</Th>
                  <Th>Equipment</Th>
                  <Th>Status</Th>
                  <Th>Date</Th>
                  <Th>Qty Used</Th>
                </tr>
              </Thead>
              <Tbody>
                {wos?.data.map(wo => {
                  const partUsage = wo.parts?.find(p => p.part_id === id);
                  return (
                    <Tr
                      key={wo.id}
                      className="cursor-pointer hover:bg-brand-50"
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                    >
                      <Td className="font-mono text-xs">{wo.wo_number}</Td>
                      <Td className="font-medium text-brand-600">{wo.title}</Td>
                      <Td className="text-xs">{wo.equipment?.name ?? wo.custom_equipment_name ?? '—'}</Td>
                      <Td>
                        <Badge color={statusColor(wo.status)}>
                          {wo.status.replace(/_/g, ' ')}
                        </Badge>
                      </Td>
                      <Td className="text-xs">{fmtDate(wo.actual_end ?? wo.created_at)}</Td>
                      <Td className="text-xs">{partUsage?.quantity_used ?? '—'}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* ── Attachments ────────────────────────────────────────────────────── */}
      <AttachmentsPanel entityType="part" entityId={id!} />

    </div>
  );
}
